/* InfoBarComponent web-component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, Event, EventEmitter, h, Method, Prop } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import { AbstractComponent } from '@cewe-designsystem/abstract';
import { UtilLocalStorage } from '@cewe-designsystem/util_storage';
import parseDurationString from 'parse-duration';
import InfoBarTemplate from './cwc-info-bar.template';
import { InfoBarColorScheme } from './InfoBarColorScheme';

// default closed time if not set at component
const MINIMUM_CLOSED_TIME = 86400000; // 1 day
const DEFAULT_CLOSED_TIME = 2592000000; // 30 days

/**
 * @slot close-icon - HTML element used as caption for the button to close the infobar e.g. an img or svg
 */
@Component({
  tag: 'cwc-component-info-bar',
  styleUrl: './cwc-info-bar.scss',
  shadow: true,
})
export class InfoBarComponent {
  /**
   * unique id of the bar to identify it in cookies.
   */
  @Prop({ reflect: true }) barId: string;

  /**
   * selected color scheme.
   */
  @Prop({ reflect: true }) colorScheme: InfoBarColorScheme = InfoBarColorScheme.BLUE;

  /**
   * indicates if the bar is closable by user.
   */
  @Prop() isClosable: boolean = false;

  /**
   * time to live until a closed bar will reappear.
   * the value must be a string of 1d (day), 1w (week), 1y (year)
   * @see https://www.npmjs.com/package/parse-duration
   */
  @Prop() closeTtl: string;

  /**
   * indicates if the bar is visible.
   */
  @Prop({ reflect: true }) opened: boolean = true;

  /**
   * the custom element itself.
   */
  @Element()
  element: HTMLElement;

  /**
   * Event thrown if the info-bar is closed.
   * @event on:info-bar:close
   */
  @Event({
    eventName: 'on:info-bar:close',
  })
  onInfoBarClose: EventEmitter;

  /**
   * Event thrown if a link inside the info-bar is clicked.
   * @event on:info-bar:link-click
   */
  @Event({
    eventName: 'on:info-bar:link-click',
  })
  onInfoBarLinkClick: EventEmitter;

  /**
   * unique ID of the info-bar used for cookies.
   */
  get uniqueId() {
    return `ComponentInfoBar_${this.barId}`;
  }

  /**
   * convert closeTtl in milliseconds.
   */
  get closeTimeInMilliseconds() {
    return this.closeTtl ? Math.max(MINIMUM_CLOSED_TIME, parseDurationString(this.closeTtl, 'ms')) : null;
  }

  /**
   * @inheritDoc
   */
  componentWillLoad() {
    if (this.isCloseCookieSet()) {
      this.opened = false;
    }

    this.registerOnLinkClickHandler();
  }

  /**
   * @inheritDoc
   */
  render() {
    if (!this.opened) {
      return '';
    }

    return <InfoBarTemplate controller={this} />;
  }

  /**
   * Click the info-bar to close
   *
   * @function close
   * @docs
   */
  @Method()
  async close() {
    this.onClose();
  }

  /**
   * close the info-bar.
   */
  onClose() {
    this.opened = false;
    this.setCloseCookie();

    /* emit the event - for tracking and things */
    this.onInfoBarClose.emit(this.element);
  }

  /**
   * sets the blocker cookie.
   */
  setCloseCookie(): void {
    const cookieTtl = this.closeTimeInMilliseconds;
    if (cookieTtl !== null) {
      UtilLocalStorage.set(this.uniqueId, '1', cookieTtl);
    } else {
      UtilLocalStorage.set(this.uniqueId, '1', DEFAULT_CLOSED_TIME);
    }
  }

  /**
   * returns true if the close cookie is set.
   */
  isCloseCookieSet(): boolean {
    //check cookie
    return UtilLocalStorage.isSet(this.uniqueId);
  }

  /**
   * register an event handler for handling click events on links.
   */
  registerOnLinkClickHandler() {
    this.element.addEventListener('click', (e) => {
      if (!(e.target instanceof HTMLElement)) {
        return;
      }

      const element = e.target as HTMLElement;
      if (element.tagName.toLowerCase() === 'a') {
        this.onInfoBarLinkClick.emit(this.element);
      }
    });
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(InfoBarComponent.prototype, AbstractComponent.prototype);

export interface InfoBarComponent extends AbstractComponent {}
